import React from 'react';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/badge/badge.module.css';
import {
  useThemeCheck,
  SapphireStyleProps,
  useSapphireStyleProps,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';
import { filterDOMProps } from 'react-aria/filterDOMProps';

export interface BadgeProps extends GlobalDomAttributes, SapphireStyleProps {
  /**
   * The label of the badge.
   */
  children: React.ReactNode;
  /**
   * Whether the badge has a saturated or faint background. Applies only when
   * `color` is not decorative.
   */
  variant?: 'primary' | 'secondary';
  /**
   * The background color of the badge.
   * Can either be semantic or one of numbered decorative colors.
   *
   * @default 'positive'
   */
  color?:
    | 'positive'
    | 'negative'
    | 'warning'
    | 'informative'
    | 'neutral'
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6
    | 7
    | 8
    | 9
    | 10
    | 11
    | 12
    | 13
    | 14
    | 15
    | 16;
  /**
   * @default 'md'
   */
  size?: 'md' | 'sm';
  id?: string;
}

export const Badge = (props: BadgeProps): JSX.Element => {
  useThemeCheck();

  const {
    children,
    variant = 'primary',
    color = 'neutral',
    size = 'md',
    ...otherProps
  } = props;
  const { styleProps } = useSapphireStyleProps(props);

  return (
    <span
      {...filterDOMProps(otherProps, { global: true })}
      role="presentation"
      className={clsx(
        styleProps.className,
        styles['sapphire-badge'],
        typeof color === 'string' &&
          color !== 'neutral' &&
          styles[`sapphire-badge--${variant}-${color}` as const],
        typeof color === 'number' &&
          styles[`sapphire-badge--decorative-${color}`],
        {
          [styles['sapphire-badge--sm']]: size === 'sm',
        }
      )}
      style={styleProps.style}
    >
      {children}
    </span>
  );
};
